import { useCallback, useEffect, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from '../../shared/adminApi';
import { ReferralSettings, SettingsSaveResult, UpdateSettingsBody } from './types';

/**
 * Referral settings (`GET /admin/referrals/settings`) — the two configurable coin
 * amounts, where they were read from, and how many live codes ride on the
 * fallback deal campaign.
 *
 * The coin amounts come from AppConfig rather than the database, so a value set
 * here takes effect on the consumer Lambdas without a redeploy (SSA-1023).
 * `coins_source` distinguishes an AppConfig-set value from the shipped default.
 */
export function useReferralSettings(env: Env, refreshKey = 0) {
  const { request } = useAdminApi();
  const [data, setData] = useState<ReferralSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await request<ReferralSettings>({
          env,
          path: '/admin/referrals/settings',
        });
        if (mounted) setData(payload);
      } catch (e) {
        if (mounted) {
          setError(e instanceof ApiError ? e.message : 'Could not load referral settings');
          setData(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [request, env, refreshKey, tick]);

  return { data, loading, error, refetch };
}

/** Save (`PUT`) the referral settings. */
export function useSettingsMutations(env: Env) {
  const { request } = useAdminApi();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(
    async (body: UpdateSettingsBody): Promise<SettingsSaveResult | null> => {
      setSaving(true);
      setError(null);
      try {
        return await request<SettingsSaveResult>({
          env,
          path: '/admin/referrals/settings',
          method: 'PUT',
          body,
        });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not save the referral settings');
        return null;
      } finally {
        setSaving(false);
      }
    },
    [request, env],
  );

  return { update, saving, error, setError };
}
